import { TrendingUpIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  FinancialSentimentAnalysisShape,
  financialSentimentAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  FinancialSentimentAnalysisProps,
  financialSentimentAnalysisCategories,
  FinancialSentimentAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isFinancialSentimentAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is FinancialSentimentAnalysisShape {
  return shape.type === financialSentimentAnalysisType;
}

export class FinancialSentimentAnalysisTool extends ToolStateNode {
  static override id = financialSentimentAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: financialSentimentAnalysisType,
    icon: TrendingUpIcon,
    label: FinancialSentimentAnalysisShapeUtil.docs.title,
    defaultColor: FinancialSentimentAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(financialSentimentAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={FinancialSentimentAnalysis}
        util={FinancialSentimentAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: financialSentimentAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class FinancialSentimentAnalysisShapeUtil extends BindableUtil<FinancialSentimentAnalysisShape> {
  static override migrations = createMigrations(financialSentimentAnalysisType);
  static override type = financialSentimentAnalysisType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Financial Sentiment Analysis",
    description:
      "Use AI to analyze the financial sentiment of the content. This determines if the post has a positive, negative, or neutral tone regarding financial topics.",
  };

  override getType() {
    return financialSentimentAnalysisType;
  }

  static override props: RecordProps<FinancialSentimentAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    financialSentimentAnalysisCategory: T.literalEnum(
      ...financialSentimentAnalysisCategories,
    ),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      financialSentimentAnalysisCategory: "Positive" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: FinancialSentimentAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <FinancialSentimentAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function FinancialSentimentAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: FinancialSentimentAnalysisShape;
  className?: string;
  updateShape: (
    shape: FinancialSentimentAnalysisShape,
    props: Partial<FinancialSentimentAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || "pink"}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={FinancialSentimentAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Financial Sentiment Analysis Category"
            options={financialSentimentAnalysisCategories}
            value={shape.props.financialSentimentAnalysisCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                financialSentimentAnalysisCategory:
                  v as FinancialSentimentAnalysisCategory,
              })
            }
            tooltip={FinancialSentimentAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
